// Shared helpers for the canvas definitions, the engine and the PDF.
// Pure functions: no imports from the app, so they run in Node for tests.
//
// Answers are one flat map per canvas: { 'field.id': 'string', 'table.r1.col': 'string', 'so.objection': '...' }.
// Every stored value is a string. A multi-choice value is a comma list ("S,D"). A number is typed text ("12,000").

export const TAG_LABEL = { M: 'Measured', E: 'Estimated', G: 'Guess' };

export const has = (x) => x !== null && x !== undefined && String(x).trim() !== '';

// key of a table cell: table id, row id (r1, r2... or a derived id such as k1), column id
export const key = (table, row, col) => `${table}.${row}.${col}`;

// trimmed string answer ('' when empty)
export const S = (v, k) => (v && v[k] !== null && v[k] !== undefined ? String(v[k]).trim() : '');

// number from typed text: "12,000" -> 12000, "5%" -> 5, "S$60" -> 60, "" -> null
export function toNum(s) {
  if (s === null || s === undefined) return null;
  if (typeof s === 'number') return Number.isFinite(s) ? s : null;
  const t = String(s).replace(/,/g, '').replace(/[^0-9.-]/g, '');
  if (t === '' || t === '-' || t === '.') return null;
  const x = Number(t);
  return Number.isFinite(x) ? x : null;
}
export const N = (v, k) => toNum(S(v, k));

// round half up (2.5 -> 3, 1.65 -> 1.7), the way the printed canvases and the workbook read results
export function roundHalfUp(x, dp = 0) {
  const f = 10 ** dp;
  return (Math.sign(x) * Math.round(Math.abs(x) * f + 1e-9)) / f;
}
export const fix = (x, dp = 0) => roundHalfUp(x, dp).toFixed(dp);

// 12000 -> "12,000"
export function thousands(x, dp = 0) {
  if (x === null || x === undefined || Number.isNaN(x)) return '';
  const s = fix(x, dp);
  const [i, d] = s.split('.');
  const withCommas = i.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return d ? `${withCommas}.${d}` : withCommas;
}

// money in millions: moneyM(720000, 'S$') -> "S$0.72M"; moneyM(0.36e6, 'S$') -> "S$0.36M"
export function moneyM(x, cur = 'S$', dp = 2) {
  if (x === null || x === undefined || Number.isNaN(x)) return '';
  return `${cur}${fix(x / 1e6, dp)}M`;
}

// "S,D" -> ['S','D']
export const listOf = (s) =>
  String(s || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

// non-empty rows of a user-managed table, sorted by row number: [{ id:'r1', n:1, col: 'value', ... }]
// (derived and fixed tables use ids of the form k1, cust and so on; they work too)
export function rows(v, table) {
  const prefix = `${table}.`;
  const map = new Map();
  for (const k of Object.keys(v || {})) {
    if (!k.startsWith(prefix)) continue;
    const rest = k.slice(prefix.length);
    const i = rest.indexOf('.');
    if (i < 0) continue; // 'stages._n' is the count of revealed rows, not a cell
    const id = rest.slice(0, i);
    const col = rest.slice(i + 1);
    if (!map.has(id)) map.set(id, { id, n: parseInt(id.replace(/\D+/g, ''), 10) || 0 });
    map.get(id)[col] = String(v[k] ?? '').trim();
  }
  return [...map.values()]
    .sort((a, b) => a.n - b.n)
    .filter((r) => Object.keys(r).some((c) => c !== 'id' && c !== 'n' && r[c] !== ''));
}

// one row by id ({ id, n } with no cells when absent)
export function rowOf(v, table, id) {
  const prefix = `${table}.${id}.`;
  const out = { id, n: parseInt(String(id).replace(/\D+/g, ''), 10) || 0 };
  for (const k of Object.keys(v || {})) {
    if (k.startsWith(prefix)) out[k.slice(prefix.length)] = String(v[k] ?? '').trim();
  }
  return out;
}

// Build flat answers from row literals: T('stages', [{ stage: 'Apply', pain: '...' }, ...])
// -> { 'stages.r1.stage': 'Apply', ... }. Pass ids to use your own row ids (default r1, r2, ...).
export function T(table, list, ids) {
  const out = {};
  list.forEach((row, i) => {
    const id = ids ? ids[i] : `r${i + 1}`;
    for (const [c, val] of Object.entries(row)) {
      if (val !== null && val !== undefined && val !== '') out[key(table, id, c)] = String(val);
    }
  });
  return out;
}

// the standard sign-off keys (Canvases 1 to 8 feed the Buy-in Ledger)
export const SO = {
  reviewer: 'so.reviewer',
  objection: 'so.objection',
  evidence: 'so.evidence',
  owner: 'so.owner',
  status: 'so.status',
  star: 'so.star',
};

// evidence: a number tagged E or G goes to the Evidence Gaps list. Build entries with this.
export const gap = (item, tag, where) => ({ item, tag, where });

// Evidence Gaps list order: G first, then E, each in canvas order
export function sortGaps(list) {
  const rank = { G: 0, E: 1 };
  return list
    .filter((g) => g.tag === 'G' || g.tag === 'E')
    .map((g, i) => ({ ...g, _i: i }))
    .sort((a, b) => rank[a.tag] - rank[b.tag] || a._i - b._i)
    .map(({ _i, ...g }) => g);
}

// the open Ledger rows that go to Canvas 9: starred first, then canvas order
export function ledgerTop(ledger, n = 3) {
  return ledger
    .filter((r) => r.status === 'Open')
    .map((r, i) => ({ ...r, _i: i }))
    .sort((a, b) => Number(b.star) - Number(a.star) || a.canvas - b.canvas || a._i - b._i)
    .slice(0, n)
    .map(({ _i, ...r }) => r);
}

// small hash of any JSON-able value, used to notice that what a canvas pulled has changed
export function hashOf(x) {
  const s = JSON.stringify(x);
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

export const plural = (n, one, many) => (n === 1 ? one : many || `${one}s`);
